import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function MessageCreatorWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMemberToken] = useState(() => Boolean(localStorage.getItem("memberToken")));
  const messagesEndRef = useRef(null);
  const loadedMessages = useRef(false);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async (force = false) => {
    if ((loadedMessages.current && !force) || !hasMemberToken) return;
    setLoadingMessages(true);
    try {
      const response = await api.get("/api/messages/mine");
      const threadMessages = (response.data?.messages || []).flatMap((thread) => [
        { id: thread.id, text: thread.message, sender: "user", timestamp: thread.created_at },
        ...(thread.replies || []).map((reply) => ({
          id: reply.id,
          text: reply.message,
          sender: reply.sender || "creator",
          timestamp: reply.created_at,
        })),
      ]);
      setMessages(threadMessages);
      loadedMessages.current = true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load your messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;

    const content = message.trim();
    const userMessage = {
      id: `pending-${Date.now()}`,
      text: content,
      sender: "user",
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage("");
    setLoading(true);

    try {
      const response = await api.post("/api/messages/send", {
        subject: "Message from Members Area",
        message: content,
      });
      const sentMessage = response.data?.message;
      setMessages((current) => current.map((item) => item.id === userMessage.id ? {
        id: sentMessage?.id || userMessage.id,
        text: sentMessage?.message || content,
        sender: "user",
        timestamp: sentMessage?.created_at || userMessage.timestamp,
      } : item));
      loadedMessages.current = true;
      toast.success("Message sent to Gio");
    } catch (err) {
      console.error("Failed to send message:", err);
      toast.error(err?.response?.data?.detail || "Failed to send message. Please try again.");
      setMessages((current) => current.filter((item) => item.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openWidget = () => {
    setIsOpen(true);
    loadMessages(true);
  };

  if (!hasMemberToken) return null;

  return (
    <>
      {/* Floating Button - Bottom Right */}
      {!isOpen && (
        <button
          onClick={openWidget}
          className="fixed bottom-6 right-6 p-4 rounded-full shadow-2xl border-2 transition hover:scale-110 z-40 animate-pulse"
          style={{
            background: "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)",
            borderColor: "rgba(255, 255, 255, 0.2)",
          }}
          title="Message the Creator"
          aria-label="Message the Creator">
          <MessageCircle size={24} className="text-white" />
        </button>
      )}

      {/* Chat Panel - Copilot Style */}
      {isOpen && (
        <div 
          className="fixed bottom-4 right-4 z-40 flex h-[min(600px,calc(100vh-2rem))] w-[calc(100vw-2rem)] max-w-96 flex-col rounded-lg border shadow-2xl sm:bottom-6 sm:right-6"
          style={{
            background: "rgba(24, 24, 27, 0.98)",
            backdropFilter: "blur(20px)",
            borderColor: "rgba(236, 72, 153, 0.3)",
          }}>
          
          {/* Header */}
          <div 
            className="flex items-center justify-between p-4 border-b rounded-t-2xl"
            style={{
              background: "linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%)",
              borderColor: "rgba(113, 113, 122, 0.2)"
            }}>
            <div className="flex items-center gap-3">
              <div className="relative">
                <MessageCircle size={20} style={{ color: "#ec4899" }} />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Message Creator</h3>
                <p className="text-xs text-zinc-400">Direct line to Gio</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Close messages"
              className="p-1.5 rounded-lg hover:bg-zinc-800 transition"
              style={{ color: "#71717a" }}>
              <X size={18} />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ 
            background: "rgba(9, 9, 11, 0.4)",
          }}>
            {loadingMessages && messages.length === 0 && <p className="text-center text-sm text-zinc-500">Loading your conversation…</p>}
            {messages.length === 0 && (
              <div className="text-center py-12">
                <MessageCircle size={48} className="mx-auto mb-4 text-zinc-700" />
                <p className="text-zinc-500 text-sm mb-2">Start a conversation</p>
                <p className="text-zinc-600 text-xs">
                  Ask questions, give feedback, or just say hi!
                </p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.sender === "user"
                      ? "rounded-br-sm"
                      : "rounded-bl-sm"
                  }`}
                  style={{
                    background: msg.sender === "user"
                      ? "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)"
                      : "rgba(63, 63, 70, 0.5)",
                    color: "#fff",
                    border: "none"
                  }}>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.text}
                  </p>
                  <p className="text-xs mt-1 opacity-70">
                    {new Date(msg.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div 
                  className="px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-2"
                  style={{ background: "rgba(63, 63, 70, 0.5)" }}>
                  <Loader2 size={16} className="animate-spin text-purple-400" />
                  <span className="text-sm text-zinc-400">Sending...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t" style={{ borderColor: "rgba(113, 113, 122, 0.2)" }}>
            <div className="flex items-end gap-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={loading}
                rows={2}
                className="flex-1 px-4 py-3 rounded-xl resize-none focus:outline-none focus:ring-2 transition disabled:opacity-50"
                style={{
                  background: "rgba(39, 39, 42, 0.8)",
                  color: "#fff",
                  border: "1px solid rgba(113, 113, 122, 0.3)",
                  focusRing: "rgba(236, 72, 153, 0.5)"
                }}
              />
              <button
                onClick={sendMessage}
                aria-label="Send message"
                disabled={!message.trim() || loading}
                className="p-3 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                style={{
                  background: message.trim() && !loading
                    ? "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)"
                    : "rgba(63, 63, 70, 0.5)",
                }}>
                {loading ? (
                  <Loader2 size={20} className="text-white animate-spin" />
                ) : (
                  <Send size={20} className="text-white" />
                )}
              </button>
            </div>
            <p className="text-xs text-zinc-600 mt-2">
              💡 Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      )}
    </>
  );
}
